/**
 * Regelversion 7: Vorwahlkampf in den letzten Monaten der Legislatur.
 *
 * Sechs Monate vor dem Ende der Legislatur beginnt der Vorwahlkampf: drei TV-Duelle mit den stärksten
 * Oppositionsparteien, je eines alle zwei Monate. Jedes Duell hat drei Fragen. Danach zeigt eine
 * Blitzumfrage, wer überzeugt hat, und das Ergebnis wirkt sofort auf die Umfragen.
 * Versprechen aus den Duellen gelten im Wahlkampf weiter.
 * Nur Einzelspieler.
 */
import { MONTHS } from '../content/consts';
import { OUTLETS } from '../content/parties';
import { buildDuel } from './duel';
import { duelEffect } from './campaign';
import { oppIds } from './opp5';
import { PA, PN } from './rules';
import { S, log } from './state';
import { clamp, dateLabel } from './util';
import { lvlHas } from './level12';
import { since } from './version';
import { press7 } from './news7';

export type PreDuel = { opp: string; at: number; q: any[]; ans: { o: number; v: number; t: string }[]; done: boolean; poll?: number };
export type Pre = { leg: number; duels: PreDuel[]; prom: any[]; tot: number };

export const preOn = (): boolean => since(7) && !S.mp && lvlHas('pre'); // Version 12: ab Stufe 2
export const PRE_START = 6, PRE_EVERY = 2, PRE_Q = 3;
const cur = (): Pre | null => (S.pre && S.pre.leg === S.leg ? S.pre : null);

/** Das Duell, das jetzt ansteht (blockiert die Zeit, bis es gespielt ist). */
export function preDue(): PreDuel | null {
  const P = cur(); if (!P || S.phase !== 'govern') return null;
  return P.duels.find((d) => !d.done && S.m >= d.at) || null;
}

/** Monatlich: Vorwahlkampf eröffnen und Duelle ankündigen. */
export function preTick(): boolean {
  if (!preOn() || S.phase !== 'govern') return false;
  const rel = S.m - S.m0;
  if (!cur() && rel >= MONTHS - PRE_START) {
    const opps = oppIds().sort((a, b) => (S.res.seats[b] || 0) - (S.res.seats[a] || 0)).slice(0, 3);
    if (!opps.length) return false;
    const duels: PreDuel[] = opps.map((opp, i) => ({ opp, at: S.m + i * PRE_EVERY, q: [], ans: [], done: false }));
    S.pre = { leg: S.leg, duels, prom: [], tot: 0 };
    log(`Der Vorwahlkampf beginnt. TV-Duelle mit ${opps.map(PA).join(', ')} bis ${dateLabel(duels[duels.length - 1].at)}.`);
  }
  const d = preDue();
  if (d && !d.q.length) {
    d.q = buildDuel({ first: false, gov: true, opp: PA(d.opp), coalition: S.partners.length > 0 }).slice(0, PRE_Q);
    log(`Heute Abend: TV-Duell gegen ${PN(d.opp)}.`);
    return true;
  }
  return !!d;
}

/** Antwort im laufenden Duell. */
export function preAnswer(i: number) {
  const P = cur(), d = preDue(); if (!P || !d || !d.q.length) return;
  const q = d.q[d.ans.length]; if (!q) return;
  const o = q.o[clamp(Math.round(i), 0, q.o.length - 1)];
  const save = S.camp; S.camp = { first: false, promise: P.prom } as any;
  let r: [number, string] = [0, ''];
  try { r = duelEffect(o) as [number, string]; } finally { S.camp = save; }
  d.ans.push({ o: clamp(Math.round(i), 0, q.o.length - 1), v: r[0], t: r[1] });
  if (d.ans.length >= d.q.length) {
    const sum = clamp(d.ans.reduce((s, a) => s + a.v, 0), -3, 3), eff = Math.round(sum * .4 * 10) / 10;
    d.done = true; d.poll = Math.round(clamp(50 + sum * 8, 22, 78));
    S.basis += eff; P.tot = Math.round((P.tot + eff) * 10) / 10;
    const won = d.poll >= 50;
    log(`TV-Duell gegen ${PN(d.opp)}: In der Blitzumfrage überzeugt ${won ? S.name : PN(d.opp)} (${won ? d.poll : 100 - d.poll} zu ${won ? 100 - d.poll : d.poll} Prozent).`, won ? 'good' : 'bad');
    press7(OUTLETS[won ? 2 : 0].n, won ? `Klarer Punktsieg im TV-Duell gegen ${PA(d.opp)}` : `TV-Duell: ${PA(d.opp)} stiehlt der Regierung die Show`, won ? .8 : -.8);
  }
}

/** Wahlkampfbeginn: Versprechen aus dem Vorwahlkampf übernehmen. */
export function preToCampaign(c: any) {
  const P = cur(); if (!preOn() || !P) return;
  for (const p of P.prom) if (!c.promise.some((x: any) => x.k === p.k && x.iss === p.iss)) c.promise.push(p);
}
